import { api } from '@/api/client';
import { extractApiError } from '@/api/errorStatus';
import { billingOverviewSchema } from '@/api/schemas/billing.schema';
import { billingPlansSchema } from '@/api/schemas/billingPlans.schema';
import { parseOrThrow } from '@/api/schemas/parseOrThrow';
import type { components } from '@/api/generated/schema';
import type {
  BillingResponse,
  TransactionListResponse,
  CheckoutResponse,
  CheckoutPeriod,
  PortalResponse,
  CreditsPurchaseRequest,
  CreditsPurchaseError,
  AiCostCatalogResponse,
  BillingPlansResponse,
} from '@/types/billing';

/** WEB-3298 юнит 2: ответы ручек долга по плате за объём — из контракта API. */
export type UsageDebtStatus = components['schemas']['UsageDebtStatusResponseDto'];
export type PayUsageDebtResult = components['schemas']['PayUsageDebtResponseDto'];

export interface TransactionQueryParams {
  /**
   * WEB-1131: membership-checked org scope (mirrors GET /billing's orgId,
   * WEB-660 on the backend — GetTransactionsQueryDto already accepts it).
   */
  orgId?: string;
  from?: string;
  to?: string;
  type?: string;
  page?: number;
  limit?: number;
}

export const billingApi = {
  // WEB-575: response validated against the codegen-pinned zod schema
  // (billingOverviewSchema) — a malformed/renamed field throws loudly here
  // instead of silently rendering as blank/undefined (WEB-435 class of bug).
  // WEB-1131 (деньги/права): orgId — membership-checked scope на сервере
  // (billing.controller.ts:87, WEB-616). Раньше сюда прокидывался только
  // projectId — сервер тогда сам резолвил «активную» орг вызывающего, что и
  // давало админу нескольких организаций чужие цифры под чужим ярлыком.
  get: async (projectId?: string, orgId?: string) => {
    const params: Record<string, string> = {};
    if (projectId) params.projectId = projectId;
    if (orgId) params.orgId = orgId;
    const res = await api.get<BillingResponse>('/billing', {
      params: Object.keys(params).length > 0 ? params : undefined,
    });
    return {
      ...res,
      data: parseOrThrow(billingOverviewSchema, res.data, 'GET /billing') as BillingResponse,
    };
  },

  getTransactions: (params: TransactionQueryParams = {}) =>
    api.get<TransactionListResponse>('/billing/transactions', { params }),

  /**
   * WEB-611 (money HIGH): live Pro price read from Stripe by the
   * backend — root-cause fix for displayed price != charged price (the
   * frontend previously hardcoded these amounts). Response validated
   * against the codegen-pinned zod schema (same WEB-575 pattern as `get`
   * above) since this is money-critical, user-facing display data.
   */
  getPlans: async () => {
    const res = await api.get<BillingPlansResponse>('/billing/plans');
    return {
      ...res,
      data: parseOrThrow(
        billingPlansSchema,
        res.data,
        'GET /billing/plans',
      ) as BillingPlansResponse,
    };
  },

  /**
   * WEB-4008: `period` — только с карточки тарифа; не задан — тело прежнее,
   * без ключа (сервер берёт помесячно), окно тарифов шлёт как раньше.
   * WEB-3302 (после WEB-2504): оформляется только PRO.
   */
  createCheckout: (
    projectId: string,
    tier: 'PRO',
    period?: CheckoutPeriod,
  ) =>
    api.post<CheckoutResponse>(
      '/billing/checkout',
      period === undefined ? { tier, projectId } : { tier, projectId, period },
    ),

  createPortal: (projectId: string) =>
    api.post<PortalResponse>('/billing/portal', { projectId }),

  /**
   * WEB-1094 (деньги) — owner-toggle «оплачивать тариф с баланса, если
   * карта не прошла». RBAC (org Owner/Admin) enforced backend-side.
   */
  setPayFromBalanceToggle: (projectId: string, enabled: boolean) =>
    api.post<{ enabled: boolean }>('/billing/pay-from-balance-toggle', {
      projectId,
      enabled,
    }),

  /**
   * WEB-3298 юнит 2 (деньги) — «Оплатить долг» по плате за объём: сервер
   * списывает сохранённую карту, при отказе или 3-D Secure отдаёт ссылку на
   * страницу счёта Stripe. RBAC (владелец или администратор с доступом к
   * деньгам) — на сервере.
   */
  payUsageDebt: (projectId: string) =>
    api.post<PayUsageDebtResult>('/billing/usage-debt/pay', { projectId }),

  /** WEB-3298 юнит 2 — сводка долга по объёму для плашки кабинета. */
  getUsageDebt: (projectId: string) =>
    api.get<UsageDebtStatus>('/billing/usage-debt', { params: { projectId } }),

  /**
   * Fetch the AI operation cost catalog from the backend.
   * WEB-1367: ручка реализована (cost-catalog.controller.ts:21-31) — прежняя
   * пометка «not yet implemented» снята как пережиток.
   */
  getCostCatalog: () => api.get<AiCostCatalogResponse>('/ai-funnels/cost-catalog'),

  /**
   * Purchase wallet credits (top-up). Gated behind TOPUP_ENABLED on the
   * backend: returns 404 when the feature is off, and 402 for FREE-plan
   * projects that must upgrade first. Rejections are normalized to
   * {@link CreditsPurchaseError} (разобранный отказ со `status`, `code` и
   * `reasonCode`) so the UI can branch on `status` and speak by code.
   */
  createCreditsPurchase: async (
    request: CreditsPurchaseRequest,
  ): Promise<{ data: CheckoutResponse }> => {
    try {
      return await api.post<CheckoutResponse>('/billing/credits/purchase', {
        package: request.package,
        projectId: request.projectId,
      });
    } catch (error: unknown) {
      // WEB-1954: отдаём ВЕСЬ разобранный отказ. Раньше отсюда уходили только
      // status и message — `code`/`reasonCode` умирали в самом начале цепочки,
      // и экран пополнения по построению не мог спросить реестр кодов.
      const purchaseError: CreditsPurchaseError = extractApiError(error);
      return Promise.reject(purchaseError);
    }
  },
};
